import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Edit2 } from "lucide-react";

const TVA_RATES = [
  { value: "0.21", label: "21%" },
  { value: "0.12", label: "12%" },
  { value: "0.06", label: "6%" },
  { value: "0", label: "0%" },
];

const empty = () => ({ nom: "", description: "", prix_unitaire: 0, taux_tva: 0.21, keywords: "", actif: true });

const ServicesTab = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [services, setServices] = useState([]);
  const [ventes, setVentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const fetchAll = async () => {
    try {
      const [s, v] = await Promise.all([
        axios.get(`${API}/services`, { headers }),
        axios.get(`${API}/ventes`, { headers }),
      ]);
      setServices(s.data);
      setVentes(v.data);
    } catch { toast.error("Erreur chargement services"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  // Aggregate ventes per service (current month)
  const now = new Date();
  const curMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const statsByService = services.reduce((acc, s) => {
    const vForService = ventes.filter(v => v.type_item === "service" && v.service_id === s.id);
    const vMonth = vForService.filter(v => v.mois === curMonth);
    acc[s.id] = {
      total_qty: vForService.reduce((a, v) => a + (v.quantite || 0), 0),
      total_ca: vForService.reduce((a, v) => a + (v.ca_ttc || 0), 0),
      month_qty: vMonth.reduce((a, v) => a + (v.quantite || 0), 0),
      month_ca: vMonth.reduce((a, v) => a + (v.ca_ttc || 0), 0),
    };
    return acc;
  }, {});

  const openCreate = () => { setEditing(null); setForm(empty()); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      nom: s.nom, description: s.description || "",
      prix_unitaire: s.prix_unitaire, taux_tva: s.taux_tva,
      keywords: (s.keywords || []).join(", "),
      actif: s.actif !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom) { toast.error("Le nom est requis"); return; }
    const payload = {
      nom: form.nom,
      description: form.description,
      prix_unitaire: Number(form.prix_unitaire) || 0,
      taux_tva: Number(form.taux_tva) || 0.21,
      keywords: form.keywords.split(",").map(k => k.trim().toLowerCase()).filter(Boolean),
      actif: form.actif,
    };
    try {
      if (editing) {
        await axios.put(`${API}/services/${editing.id}`, payload, { headers });
        toast.success("Service mis à jour");
      } else {
        await axios.post(`${API}/services`, payload, { headers });
        toast.success("Service créé");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Supprimer le service "${s.nom}" ?`)) return;
    try {
      await axios.delete(`${API}/services/${s.id}`, { headers });
      toast.success("Service supprimé");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  return (
    <Card data-testid="services-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Services ({services.length})
          </CardTitle>
          <CardDescription>
            Prestations non-physiques (assurance, livraison, montage, etc.) — sans gestion de stock, mais comptabilisées dans les ventes.
          </CardDescription>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={openCreate} data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="service-dialog">
            <DialogHeader><DialogTitle>{editing ? "Modifier le service" : "Nouveau service"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              <div><Label>Nom *</Label><Input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Ex: Assurance casse" required data-testid="service-nom" /></div>
              <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Ex: Assurance bris 2 ans" data-testid="service-desc" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prix unitaire (HT)</Label><Input type="number" step="0.01" value={form.prix_unitaire} onChange={e => setForm({ ...form, prix_unitaire: e.target.value })} data-testid="service-prix" /></div>
                <div>
                  <Label>TVA</Label>
                  <Select value={String(form.taux_tva)} onValueChange={v => setForm({ ...form, taux_tva: Number(v) })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TVA_RATES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Mots-clés de reconnaissance (séparés par des virgules)</Label>
                <Input value={form.keywords} onChange={e => setForm({ ...form, keywords: e.target.value })} placeholder="assurance, casse, bris" data-testid="service-keywords" />
                <p className="text-xs text-slate-400 mt-1">
                  Optionnel — si tu mets "assurance, bris, casse", toute ligne de devis contenant un de ces mots sera automatiquement liée à ce service.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <Label>Actif</Label>
                <Switch checked={form.actif} onCheckedChange={v => setForm({ ...form, actif: v })} data-testid="service-actif" />
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="submit-service">
                {editing ? "Enregistrer" : "Créer le service"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-slate-400">Chargement…</div>
        ) : services.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="mb-1">Aucun service</p>
            <p className="text-xs">Crée ton premier service (ex: Assurance casse, Livraison, Montage)</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Mots-clés</TableHead>
                  <TableHead className="text-right">Prix HT</TableHead>
                  <TableHead className="text-center">TVA</TableHead>
                  <TableHead className="text-right">Vendus ce mois</TableHead>
                  <TableHead className="text-right">CA ce mois</TableHead>
                  <TableHead className="text-right">Total CA</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map(s => {
                  const st = statsByService[s.id] || {};
                  return (
                    <TableRow key={s.id} data-testid={`service-row-${s.id}`}>
                      <TableCell>
                        <div className="font-medium">{s.nom}</div>
                        {s.description && <div className="text-xs text-slate-400">{s.description}</div>}
                        {!s.actif && <span className="text-xs text-red-500">(inactif)</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(s.keywords || []).slice(0, 5).map(k => (
                            <span key={k} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{k}</span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{s.prix_unitaire.toFixed(2)} €</TableCell>
                      <TableCell className="text-center text-sm text-slate-500">{(s.taux_tva * 100).toFixed(0)}%</TableCell>
                      <TableCell className="text-right font-medium">{st.month_qty || 0}</TableCell>
                      <TableCell className="text-right text-emerald-600">{(st.month_ca || 0).toFixed(2)} €</TableCell>
                      <TableCell className="text-right text-slate-500">{(st.total_ca || 0).toFixed(2)} €</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Edit2 className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(s)} className="text-red-500" data-testid={`delete-service-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ServicesTab;
